import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spin, Alert, Pagination } from "antd";
import BannerCarousel from "../components/BannerCarousel";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/Home.css";
import { Contact } from "../components/Contact";
import AnimatedElement from "../components/AnimatedElement";
import { publicacoesAPI, parceirosAPI } from "../services/api";
import { useTranslation } from "react-i18next";

// Definição de constantes para paginação
const ITEMS_PER_PAGE_DESKTOP = 6;
const ITEMS_PER_PAGE_MOBILE = 3;

export const CuidadosSaudeAtencaoPrimaria = () => {
  const { t, i18n } = useTranslation();
  const [activeTabCSAP, setActiveTabCSAP] = useState("como-funciona-csap");
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [publicacoes, setPublicacoes] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingParceiros, setLoadingParceiros] = useState(false);
  const [error, setError] = useState(null);
  const [errorParceiros, setErrorParceiros] = useState(null);

  // Estado para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchPublicacoes();
    fetchParceiros();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accordionData = [
    {
      id: "como-funciona-csap",
      title: t("csap.accordion.howItWorks.title"),
      heading: t("csap.accordion.howItWorks.heading"),
      body: t("csap.accordion.howItWorks.content"),
      list: [
        {
          label: t("csap.accordion.howItWorks.list.doctors.label"),
          text: t("csap.accordion.howItWorks.list.doctors.text"),
        },
        {
          label: t("csap.accordion.howItWorks.list.managers.label"),
          text: t("csap.accordion.howItWorks.list.managers.text"),
        },
      ],
      image:
        i18n.language === "en"
          ? "/assets/img/product-images/csap1.translated.png"
          : "/assets/img/product-images/csap1.png",
    },
    {
      id: "nossa-abordagem-csap",
      title: t("csap.accordion.ourApproach.title"),
      heading: t("csap.accordion.ourApproach.heading"),
      body: t("csap.accordion.ourApproach.content"),
      // Print da tela do notebook "DSD – Gestão Inteligente"
      image:
        i18n.language === "en"
          ? "/assets/img/banners/smart_management_banner_en.png"
          : "/assets/img/banners/smart_management_banner.png",
    },
    {
      id: "nossa-ciencia-csap",
      title: t("csap.accordion.ourScience.title"),
      heading: t("csap.accordion.ourScience.heading"),
      body: t("csap.accordion.ourScience.content"),
      // TODO: imagem do Módulo 4 ("em construção") pendente (ver docs/alteracoes-site-dsd.md)
      modules: [
        {
          label: t("csap.modules.module1.label"),
          text: t("csap.modules.module1.text"),
          image: "/assets/img/banners/m1_banner.png",
        },
        {
          label: t("csap.modules.module2.label"),
          text: t("csap.modules.module2.text"),
          image: "/assets/img/banners/m2_banner.png",
        },
        {
          label: t("csap.modules.module3.label"),
          text: t("csap.modules.module3.text"),
          image: "/assets/img/banners/m3_banner.png",
        },
        {
          label: t("csap.modules.module4.label"),
          text: t("csap.modules.module4.text"),
          badge: t("csap.modules.module4.badge"),
        },
      ],
    },
  ];

  const renderTabTextBlock = (item) => (
    <>
      {item.heading && <h4 className="tab-content-heading">{item.heading}</h4>}
      <p>{item.body}</p>
      {item.list && (
        <ul className="tab-content-list">
          {item.list.map((li, idx) => (
            <li key={idx}>
              <strong>{li.label}:</strong> {li.text}
            </li>
          ))}
        </ul>
      )}
      {item.modules && (
        <div className="modules-zigzag">
          {item.modules.map((mod, idx) => (
            <div
              className={`module-row${idx % 2 === 1 ? " reverse" : ""}`}
              key={idx}
            >
              {mod.image ? (
                <div className="module-image">
                  <img src={mod.image} alt={mod.label} />
                </div>
              ) : (
                <div className="module-image-placeholder">
                  {mod.badge && (
                    <span className="module-badge">{mod.badge}</span>
                  )}
                  <span className="module-placeholder-text">
                    {t("csap.modules.imagePending")}
                  </span>
                </div>
              )}
              <div className="module-text">
                <h5>{mod.label}</h5>
                <p>{mod.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const fetchPublicacoes = async () => {
    setLoading(true);
    try {
      const response = await publicacoesAPI.getAll("CSAP");
      setPublicacoes(response.data);
      setError(null);
    } catch (err) {
      console.error("Erro ao carregar publicações:", err);
      setError(t("csap.errors.loadPublications"));
    } finally {
      setLoading(false);
    }
  };

  const fetchParceiros = async () => {
    setLoadingParceiros(true);
    try {
      const response = await parceirosAPI.getAll();
      // Filtramos apenas os parceiros relacionados ao CSAP
      const parceirosCSAP = response.data.filter(
        (parceiro) => parceiro.produto === "CSAP"
      );
      setParceiros(parceirosCSAP);
      setErrorParceiros(null);
    } catch (err) {
      console.error("Erro ao carregar parceiros:", err);
      setErrorParceiros(t("csap.errors.loadPartners"));
    } finally {
      setLoadingParceiros(false);
    }
  };

  // Banner estático para Cuidados de Saúde da Atenção Primária
  const banner = [
    {
      title: "",
      subtitle: t("csap.banner.subtitle"),
      description: t("csap.banner.description"),
      image: "/assets/img/banners/CSAP.jpg",
      buttonText: t("csap.banner.buttonText"),
    },
  ];

  // Fallback para publicações estáticas caso a API falhe
  const publicacoesCSAP = [];
  // Fallback para parceiros estáticos caso a API falhe
  const parceirosEstaticos = [];

  // Converter dados da API para o formato usado no componente
  const formatPublicacoes = (data) => {
    return data.map((pub) => ({
      id: pub._id,
      title: pub.titulo,
      description:
        pub.texto.substring(0, 150) + (pub.texto.length > 150 ? "..." : ""),
      image:
        pub.imagem && pub.imagem.startsWith("http")
          ? pub.imagem
          : `${
              process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") ||
              "http://localhost:5000"
            }${pub.imagem}`,
      link: pub.link,
    }));
  };

  // Determinar quais publicações mostrar
  const publicacoesParaMostrar =
    publicacoes.length > 0 ? formatPublicacoes(publicacoes) : publicacoesCSAP;

  // Processar os parceiros
  const formatParceiros = (data) => {
    return data.map((parceiro) => ({
      _id: parceiro._id,
      nome: parceiro.nome,
      logo:
        parceiro.logo && parceiro.logo.startsWith("http")
          ? parceiro.logo
          : `${
              process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") ||
              "http://localhost:5000"
            }${parceiro.logo}`,
      link: parceiro.link,
    }));
  };

  // Determinar quais parceiros mostrar
  const parceirosParaMostrar =
    parceiros.length > 0 ? formatParceiros(parceiros) : parceirosEstaticos;

  // Verificar se deve mostrar carrossel ou grid estático
  const shouldShowCarousel = parceirosParaMostrar.length >= 4;
  // Lógica para paginação (desktop e mobile)
  const itemsPerPage = isMobile
    ? ITEMS_PER_PAGE_MOBILE
    : ITEMS_PER_PAGE_DESKTOP;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Função para lidar com a mudança de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Rolagem suave para o topo da seção de publicações
    document.querySelector(".publicacoes-plataforma").scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  // Itens a serem exibidos na página atual (desktop e mobile)
  const paginatedPublicacoes = publicacoesParaMostrar.slice(
    startIndex,
    endIndex
  );

  return (
    <div className="home" style={{ marginTop: 80 }}>
      {/* Lightbox para imagem ampliada com controles de zoom - SOMENTE MOBILE */}
      {isMobile && lightboxImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            cursor: dragging ? "grabbing" : "zoom-out",
            overflow: "hidden",
          }}
          onClick={() => {
            setLightboxImage(null);
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            style={{
              maxWidth: "96vw",
              maxHeight: "80vh",
              borderRadius: 12,
              boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              transform: `scale(${zoom}) translate(${pan.x / zoom}px,${
                pan.y / zoom
              }px)`,
              transition: dragging ? "none" : "transform 0.2s",
              cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "default",
              touchAction: "none",
              background: "#fff",
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              if (zoom <= 1) return;
              setDragging(true);
              setStartDrag({ x: e.clientX, y: e.clientY });
              setStartPan({ ...pan });
            }}
            onMouseMove={(e) => {
              if (!dragging) return;
              setPan({
                x: startPan.x + (e.clientX - startDrag.x),
                y: startPan.y + (e.clientY - startDrag.y),
              });
            }}
            onMouseUp={() => setDragging(false)}
            onMouseLeave={() => setDragging(false)}
            onTouchStart={(e) => {
              if (zoom <= 1) return;
              setDragging(true);
              setStartDrag({
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
              });
              setStartPan({ ...pan });
            }}
            onTouchMove={(e) => {
              if (!dragging) return;
              setPan({
                x: startPan.x + (e.touches[0].clientX - startDrag.x),
                y: startPan.y + (e.touches[0].clientY - startDrag.y),
              });
            }}
            onTouchEnd={() => setDragging(false)}
            draggable={false}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 20,
              marginTop: 24,
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
              style={{
                fontSize: 28,
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 50,
                width: 54,
                height: 54,
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.15)",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#1565c0")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#1976d2")}
              aria-label="Diminuir zoom"
            >
              –
            </button>
            <button
              onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
              style={{
                fontSize: 28,
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 50,
                width: 54,
                height: 54,
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.15)",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#1565c0")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#1976d2")}
              aria-label="Aumentar zoom"
            >
              +
            </button>
            <button
              onClick={() => {
                setLightboxImage(null);
                setZoom(1);
                setPan({ x: 0, y: 0 });
              }}
              style={{
                fontSize: 32,
                background: "#fff",
                color: "#1976d2",
                border: "2px solid #1976d2",
                borderRadius: 50,
                width: 54,
                height: 54,
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.10)",
                transition: "background 0.2s, color 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#1976d2";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = "#1976d2";
              }}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <Navbar />
      <div id="banner-home">
        <BannerCarousel banners={banner} />
      </div>
      <section className="plataforma-rha">
        <div className="container">
          <AnimatedElement animation="fade-up">
            <div className="plataforma-content">
              <div className="plataforma-header">
                <h2>{t("csap.platform.title")}</h2>
                <p>{t("csap.platform.subtitle")}</p>
              </div>
              <Card className="plataforma-tabs-card">
                {isMobile ? (
                  <div className="faq-container">
                    {accordionData.map((item) => (
                      <div
                        key={item.id}
                        className={`faq-item ${
                          activeAccordion === item.id ? "active" : ""
                        }`}
                      >
                        <div
                          className="faq-question"
                          onClick={() =>
                            setActiveAccordion(
                              activeAccordion === item.id ? null : item.id
                            )
                          }
                        >
                          <h4>{item.title}</h4>
                          <div className="faq-icon">
                            <span
                              className={`faq-arrow ${
                                activeAccordion === item.id ? "rotated" : ""
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        </div>
                        <div
                          className={`faq-answer ${
                            activeAccordion === item.id ? "expanded" : ""
                          }`}
                        >
                          <div className="faq-answer-content">
                            {renderTabTextBlock(item)}
                            {item.image && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  marginTop: 12,
                                  marginBottom: 12,
                                }}
                              >
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  style={{
                                    maxWidth: 320,
                                    width: "80vw",
                                    height: "auto",
                                    maxHeight: 140,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                                    cursor: "zoom-in",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLightboxImage({
                                      src: item.image,
                                      alt: item.title,
                                    });
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="plataforma-tabs">
                      <div
                        className={`tab-item ${
                          activeTabCSAP === "como-funciona-csap" ? "active" : ""
                        }`}
                        onClick={() => setActiveTabCSAP("como-funciona-csap")}
                      >
                        <h3>{t("csap.accordion.howItWorks.title")}</h3>
                      </div>
                      <div
                        className={`tab-item ${
                          activeTabCSAP === "nossa-abordagem-csap"
                            ? "active"
                            : ""
                        }`}
                        onClick={() => setActiveTabCSAP("nossa-abordagem-csap")}
                      >
                        <h3>{t("csap.accordion.ourApproach.title")}</h3>
                      </div>
                      <div
                        className={`tab-item ${
                          activeTabCSAP === "nossa-ciencia-csap" ? "active" : ""
                        }`}
                        onClick={() => setActiveTabCSAP("nossa-ciencia-csap")}
                      >
                        <h3>{t("csap.accordion.ourScience.title")}</h3>
                      </div>
                    </div>
                    <div className="plataforma-details">
                      <div className="tab-text-content">
                        {renderTabTextBlock(
                          accordionData.find(
                            (item) => item.id === activeTabCSAP
                          )
                        )}
                        {accordionData.find((item) => item.id === activeTabCSAP)
                          .image && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              marginTop: 16,
                            }}
                          >
                            <img
                              src={
                                accordionData.find(
                                  (item) => item.id === activeTabCSAP
                                ).image
                              }
                              alt={
                                accordionData.find(
                                  (item) => item.id === activeTabCSAP
                                ).title
                              }
                              style={{
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: 8,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Card>
              {/* Publicações CSAP */}
              <div className="publicacoes-plataforma">
                <div className="publicacoes-header">
                  <h3>{t("csap.publications.title")}</h3>
                  <p>{t("csap.publications.subtitle")}</p>
                </div>
                {loading ? (
                  <div className="loading-container">
                    <Spin size="large" tip={t("csap.loading.publications")} />
                  </div>
                ) : error ? (
                  <Alert message={error} type="error" showIcon />
                ) : (
                  <>
                    <Row
                      gutter={[24, 24]}
                      style={{ margin: "auto" }}
                      className="publicacoes-grid"
                    >
                      {paginatedPublicacoes.map((publicacao) => (
                        <Col xs={24} md={12} lg={8} key={publicacao.id}>
                          <div className="publicacao-card-bg">
                            <img
                              src={publicacao.image}
                              alt={publicacao.title}
                              className="publicacao-card-image"
                              crossOrigin="anonymous"
                            />
                            <div className="publicacao-card-overlay">
                              <div className="publicacao-card-content">
                                <h4 className="publicacao-title">
                                  {publicacao.title}
                                </h4>
                                <p className="publicacao-description">
                                  {publicacao.description}
                                </p>
                                <a
                                  href={publicacao.link}
                                  className="publicacao-link"
                                >
                                  {t("csap.publications.readMore")} →
                                </a>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}{" "}
                    </Row>
                    {publicacoesParaMostrar.length >
                      (isMobile
                        ? ITEMS_PER_PAGE_MOBILE
                        : ITEMS_PER_PAGE_DESKTOP) && (
                      <div
                        className="pagination-container"
                        style={{ textAlign: "center", marginTop: "30px" }}
                      >
                        <Pagination
                          current={currentPage}
                          total={publicacoesParaMostrar.length}
                          pageSize={
                            isMobile
                              ? ITEMS_PER_PAGE_MOBILE
                              : ITEMS_PER_PAGE_DESKTOP
                          }
                          onChange={handlePageChange}
                          showSizeChanger={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </AnimatedElement>
        </div>
      </section>
      {/* Nossos Parceiros */}
      <section id="nossos-parceiros" className="parceiros-section">
        <div className="container">
          <AnimatedElement animation="fade-up">
            <div className="parceiros-header">
              <h2>{t("csap.partners.title")}</h2>
              <p>{t("csap.partners.subtitle")}</p>
            </div>{" "}
            <div
              className={
                shouldShowCarousel
                  ? "parceiros-carousel"
                  : "parceiros-grid-static"
              }
            >
              {shouldShowCarousel ? (
                <div className="parceiros-track">
                  {loadingParceiros ? (
                    <div className="loading-container">
                      <Spin size="large" tip={t("csap.loading.partners")} />
                    </div>
                  ) : errorParceiros ? (
                    <Alert message={errorParceiros} type="error" showIcon />
                  ) : (
                    // Duplicar itens para efeito de loop contínuo
                    [...parceirosParaMostrar, ...parceirosParaMostrar].map(
                      (parceiro, index) => (
                        <div
                          className="parceiro-item"
                          key={`${parceiro._id}-${index}`}
                        >
                          <img
                            src={
                              parceiro.logo?.startsWith("http")
                                ? parceiro.logo
                                : `${
                                    process.env.REACT_APP_API_URL ||
                                    "http://localhost:5000"
                                  }${parceiro.logo}`
                            }
                            alt={parceiro.nome}
                            crossOrigin="anonymous"
                          />
                        </div>
                      )
                    )
                  )}
                </div>
              ) : (
                <div className="parceiros-static-grid">
                  {loadingParceiros ? (
                    <div className="loading-container">
                      <Spin size="large" tip={t("csap.loading.partners")} />
                    </div>
                  ) : errorParceiros ? (
                    <Alert message={errorParceiros} type="error" showIcon />
                  ) : (
                    parceirosParaMostrar.map((parceiro) => (
                      <div className="parceiro-item-static" key={parceiro._id}>
                        <img
                          src={
                            parceiro.logo?.startsWith("http")
                              ? parceiro.logo
                              : `${
                                  process.env.REACT_APP_API_URL ||
                                  "http://localhost:5000"
                                }${parceiro.logo}`
                          }
                          alt={parceiro.nome}
                          crossOrigin="anonymous"
                        />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </AnimatedElement>
        </div>
      </section>
      <Contact />
      <Footer />
    </div>
  );
};
